import React, { useCallback, useEffect, useMemo, useState } from "react";
import LiveClasses from "components/live-class/LiveClasses";
import CommonHeader from "layouts/dashboard/CommonHeader";
import courseIcon from "../../assets/images/Courses.svg";
import { fetchLiveClassesForUser } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { dateString } from "helper/utils";

const LiveClassContainer = () => {
  const [liveClasses, setLiveClasses] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const navigate = useNavigate();
  const [cookies] = useCookies();

  // Navigation handler for starting/joining live classes
  const startLiveClass = useCallback((row) => {
    const route = `/room/${row.original.live_class_id}`;
    navigate(route, {
      state: {
        liveClassId: row.original.live_class_id,
        organizer: row.original.organizer,
      },
    });
  }, [navigate]);

  // Check if current time allows joining the class (within 10 minutes of start time)
  const isButtonActive = useCallback((startDate, startTime) => {
    try {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const [hours, minutes] = startTime.split(":").map(Number);

      const start = new Date(
        today.getFullYear(),
        today.getMonth(), 
        today.getDate(),
        hours,
        minutes,
        0
      );

      const diffMs = start - today;
      const diffMinutes = diffMs / 60000;

      // Allow joining 10 minutes before start time and only on the correct date
      return diffMinutes <= 10 && startDate === formattedDate;
    } catch (error) {
      console.error('Error checking button active state:', error);
      return false;
    }
  }, []);

  // Fetch all live classes for the current user
  const fetchAllLiveClasses = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const payload = { user: cookies?.userId };
      const response = await fetchLiveClassesForUser("", payload);
      
      if (response.status === 200) {
        setLiveClasses(response?.data?.classes || []);
      } else {
        setLiveClasses([]);
      }
    } catch (error) {
      console.error('Error fetching live classes:', error);
      setLiveClasses([]);
    } finally {
      setIsDataLoading(false);
    }
  }, [cookies?.userId]);

  // Table column configuration
  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Title</span>
        ),
      },
      {
        accessorKey: "date", 
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Date</span>
        ),
      },
      {
        accessorKey: "startTime",
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>
            Start Time
          </span>
        ),
      },
      {
        accessorKey: "endTime",
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>End Time</span>
        ),
      },
      {
        accessorKey: "actions",
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Action</span>
        ),
        size: "15%",
        cell: ({ row }) => {
          const isOrganizer = row.original.organizer === cookies?.userId;
          const canJoin = isButtonActive(row.original?.date, row.original?.startTime);
          
          return (
            <div>
              <Button
                onClick={() => startLiveClass(row)}
                disabled={!isOrganizer && !canJoin}
              >
                {isOrganizer ? "Start" : "Join"}
              </Button>
            </div>
          );
        },
      },
    ],
    [cookies?.userId, isButtonActive, startLiveClass]
  );

  // Fetch live classes on component mount
  useEffect(() => {
    fetchAllLiveClasses();
  }, [fetchAllLiveClasses]);

  return (
    <>
      <CommonHeader
        text="Live Classes"
        subText="Place to manage live classes"
        icon={courseIcon}
      />

      <div className="container mt-4">
        <LiveClasses
          liveClasses={liveClasses}
          columns={columns}
          isDataLoading={isDataLoading}
        />
      </div>
    </>
  );
};

export default LiveClassContainer;